import { useNavigate, useParams, useSearchParams } from 'react-router';
import { IntegrationSheet } from '../features/gps/components/IntegrationSheet';
import { SmsIntegrationSheet } from '../features/sms/components/SmsIntegrationSheet';
import { WhatsappIntegrationSheet } from '../features/whatsapp/components/WhatsappIntegrationSheet';
import type { IntegrationsOverview } from '../hooks/use-integrations-overview';
import { integrationTabPath, type IntegrationKind } from '../integrations.paths';
import { AddIntegrationSheet } from './AddIntegrationSheet';

export type IntegrationsSheet = 'add' | 'sms-install' | 'sms-settings' | 'whatsapp-install' | 'whatsapp-settings' | 'gps-install';

interface ProviderSheetProps {
  overview: IntegrationsOverview;
  /** Installing this provider (`?provider=`), or undefined for the settings of `integrationId`. */
  providerKey: string | undefined;
  integrationId: string | undefined;
  onClose: () => void;
}

/** The sheet the route opens over a tab: its catalogue, an install, or an SMS or WhatsApp provider's settings. Closing returns to the tab. */
export function IntegrationsSheets({ tab, sheet, overview }: { tab: IntegrationKind; sheet: IntegrationsSheet | undefined; overview: IntegrationsOverview }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const close = () => navigate(integrationTabPath(tab));
  const provider = { overview, onClose: close, ...installOrSettings(sheet, searchParams.get('provider'), id) };

  if (sheet === 'add') return <AddIntegrationSheet kind={tab} overview={overview} onClose={close} />;
  if (sheet === 'gps-install') return <IntegrationSheet catalogue={overview.adapters} adapterKey={searchParams.get('adapter') ?? undefined} onClose={close} />;
  if (sheet?.startsWith('whatsapp-')) return <WhatsappSheet {...provider} />;
  if (sheet?.startsWith('sms-')) return <SmsSheet {...provider} />;
  return null;
}

/** An install route names the provider; a settings route names the installation. */
function installOrSettings(sheet: IntegrationsSheet | undefined, provider: string | null, id: string | undefined) {
  const isInstall = sheet?.endsWith('-install') ?? false;
  return { providerKey: isInstall ? (provider ?? undefined) : undefined, integrationId: isInstall ? undefined : id };
}

function SmsSheet({ overview, ...props }: ProviderSheetProps) {
  const { sms, smsProviders } = overview;
  if (!smsProviders) return null;
  return (
    <SmsIntegrationSheet
      {...props}
      providers={smsProviders}
      sendingName={overview.smsSendingName}
      onChanged={sms.reload}
      onStopSending={sms.stopSending}
      onUninstall={sms.uninstall}
    />
  );
}

function WhatsappSheet({ overview, ...props }: ProviderSheetProps) {
  const { whatsapp, whatsappProviders } = overview;
  if (!whatsappProviders) return null;
  return (
    <WhatsappIntegrationSheet
      {...props}
      providers={whatsappProviders}
      sendingName={overview.whatsappSendingName}
      onChanged={whatsapp.reload}
      onStopSending={whatsapp.stopSending}
      onUninstall={whatsapp.uninstall}
    />
  );
}
